'use client';
import { useState } from 'react';
import Button from '../buttonApp/Button';
import { MuiColorInput } from 'mui-color-input';
import useFontFamilies from '../../../hooks/useFontFamilies';

const AirtableButtonSettingsModal = ({
  airtableConfig,
  isOpen,
  onClose,
  buttonSettings,
  onSettingsChange,
  buttonColor,
  onSave,
}) => {
  const fontOptions = useFontFamilies('option');
  const [activeButtonType, setActiveButtonType] = useState('back');
  const [backFile, setBackFile] = useState(null);
  const [nextFile, setNextFile] = useState(null);
  const [backUploadStatus, setBackUploadStatus] = useState('');
  const [nextUploadStatus, setNextUploadStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  if (!isOpen) return null;

  const handleButtonSettingChange = (buttonType, key, value) => {
    console.log(`Changing ${buttonType} button setting: ${key} = ${value}`);
    onSettingsChange(buttonType, key, value);
  };

  const handleFileChange = (buttonType, file) => {
    if (buttonType === 'back') {
      setBackFile(file);
    } else {
      setNextFile(file);
    }
  };

  const handleUpload = async buttonType => {
    const file = buttonType === 'back' ? backFile : nextFile;
    const setUploadStatus = buttonType === 'back' ? setBackUploadStatus : setNextUploadStatus;

    if (!file) return;

    setIsUploading(true);
    try {
      // Step 1: Get Pre-signed URL from the Backend
      const response = await fetch('/generate-signed-url', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { url } = await response.json();

      // Step 2: Upload the File to S3
      const uploadResponse = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      setUploadStatus('File uploaded successfully!');

      // Step 3: Save Uploaded File Information
      const fileUrl = url.split('?')[0]; // Remove query params to get the actual file URL

      // Step 4: Update Click Sound in Settings
      handleButtonSettingChange(buttonType, 'clickSound', fileUrl);
      handleButtonSettingChange(buttonType, 'clickSoundFilename', file.name);

      // Clear file selection
      if (buttonType === 'back') {
        setBackFile(null);
      } else {
        setNextFile(null);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = () => {
    onSave();
    onClose();
  };

  const renderButtonSettings = buttonType => {
    const settings = buttonSettings[`${buttonType}ButtonSettings`];
    const file = buttonType === 'back' ? backFile : nextFile;
    const uploadStatus = buttonType === 'back' ? backUploadStatus : nextUploadStatus;

    const btnText =
      buttonType === 'back' ? airtableConfig?.backButtonText : airtableConfig?.nextButtonText;

    return (
      <div className="button-settings-content">
        <div className="right" style={{}}>
          <Button text={btnText} settings={settings} />
        </div>
        {/* Basic Settings Row 1 */}
        <div className="row menu-card2">
          <div className="col-md-4">
            <div className="form-group">
              <label>Font Line Height:</label>
              <input
                type="number"
                className="form-control"
                value={settings?.lineHeight ?? 1}
                onChange={e =>
                  handleButtonSettingChange(buttonType, 'lineHeight', Number(e.target.value))
                }
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label>Padding:</label>
              <input
                type="number"
                className="form-control"
                value={settings?.padding ?? 10}
                onChange={e =>
                  handleButtonSettingChange(buttonType, 'padding', Number(e.target.value))
                }
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label>Margin Bottom:</label>
              <input
                type="number"
                className="form-control"
                value={settings?.marginBottom ?? 0}
                onChange={e =>
                  handleButtonSettingChange(buttonType, 'marginBottom', Number(e.target.value))
                }
              />
            </div>
          </div>
        </div>

        {/* Basic Settings Row 2 */}
        <div className="row menu-card2">
          <div className="col-md-4">
            <div className="form-group">
              <label>Corner Radius:</label>
              <input
                type="number"
                className="form-control"
                value={settings?.cornerRadius ?? 0}
                onChange={e =>
                  handleButtonSettingChange(buttonType, 'cornerRadius', Number(e.target.value))
                }
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label>Height:</label>
              <input
                type="number"
                className="form-control"
                value={settings?.height ?? 0}
                onChange={e =>
                  handleButtonSettingChange(buttonType, 'height', Number(e.target.value))
                }
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label>Width:</label>
              <input
                type="number"
                className="form-control"
                value={settings?.width ?? 0}
                onChange={e =>
                  handleButtonSettingChange(buttonType, 'width', Number(e.target.value))
                }
              />
            </div>
          </div>
        </div>

        {/* Outline Settings */}
        <div className="row menu-card2">
          <div className="col-md-4">
            <div className="form-group">
              <label className="d-flex align-items-center" style={{ height: '25px' }}>
                Outline color: (Transparent
                <input
                  type="checkbox"
                  className="ml-1 mr-1"
                  checked={settings?.outlineColor === 'transparent'}
                  onChange={e =>
                    handleButtonSettingChange(
                      buttonType,
                      'outlineColor',
                      e.target.checked ? 'transparent' : '#000000'
                    )
                  }
                />
                )
              </label>
              <MuiColorInput
                className="form-control"
                format="hex"
                value={
                  settings?.outlineColor === 'transparent'
                    ? ''
                    : settings?.outlineColor || '#000000'
                }
                onChange={newHex =>
                  handleButtonSettingChange(
                    buttonType,
                    'outlineColor',
                    newHex?.toUpperCase?.() || '#000000'
                  )
                }
                fallbackValue="#000000"
                disabled={settings?.outlineColor === 'transparent'}
                // size="small"
                isAlphaHidden={true} // This disables the alpha slider
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0px !important',
                    height: 53,
                  },
                  '& .MuiOutlinedInput-input': {
                    height: '36px',
                  },
                  '& .MuiColorInput-Button': {
                    width: '49px !important', // Increase width
                    height: '40px !important',
                  },
                  '& .MuiInputAdornment-root .MuiBox-root': {
                    width: 18,
                    height: 18,
                  },
                }}
              />
              {/* <input
                type="color"
                disabled={settings?.outlineColor === "transparent"}
                className="form-control"
                value={settings?.outlineColor === "transparent" ? "#000000" : (settings?.outlineColor || "#000000")}
                onChange={(e) => handleButtonSettingChange(buttonType, 'outlineColor', e.target.value)}
              /> */}
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label>Outline Thickness:</label>
              <input
                type="number"
                value={settings?.outlineThickness ?? 0}
                onChange={e =>
                  handleButtonSettingChange(buttonType, 'outlineThickness', Number(e.target.value))
                }
                className="form-control"
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label>Text Alignment:</label>
              <select
                value={settings?.textAlign || 'left'}
                onChange={e => handleButtonSettingChange(buttonType, 'textAlign', e.target.value)}
                className="form-select form-control"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
          </div>
        </div>

        {/* Fill Color and Font Settings */}
        <div className="row menu-card2">
          <div className="col-md-3">
            <div className="form-group">
              <label className="d-flex align-items-center" style={{ height: '25px' }}>
                Fill color: (Transparent
                <input
                  type="checkbox"
                  className="ml-1 mr-1"
                  checked={settings?.fillColor === 'transparent'}
                  onChange={e =>
                    handleButtonSettingChange(
                      buttonType,
                      'fillColor',
                      e.target.checked ? 'transparent' : '#000000'
                    )
                  }
                />
                )
              </label>
              <MuiColorInput
                className="form-control"
                format="hex"
                value={
                  settings?.fillColor === 'transparent' ? '' : settings?.fillColor || '#000000'
                }
                onChange={newHex =>
                  handleButtonSettingChange(buttonType, 'fillColor', newHex?.toUpperCase?.())
                }
                fallbackValue="#000000"
                disabled={settings?.fillColor === 'transparent'}
                // size="small"
                isAlphaHidden={true} // This disables the alpha slider
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0px !important',
                    height: 53,
                  },
                  '& .MuiOutlinedInput-input': {
                    height: '36px',
                  },
                  '& .MuiColorInput-Button': {
                    width: '49px !important', // Increase width
                    height: '40px !important',
                  },
                  '& .MuiInputAdornment-root .MuiBox-root': {
                    width: 18,
                    height: 18,
                  },
                }}
              />
              {/* <input
                type="color"
                disabled={settings?.fillColor === "transparent"}
                className="form-control"
                value={settings?.fillColor === "transparent" ? "#000000" : (settings?.fillColor || "#000000")}
                onChange={(e) => handleButtonSettingChange(buttonType, 'fillColor', e.target.value)}
              /> */}
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label>Font Family:</label>
              <select
                value={settings?.font || 'Arial'}
                onChange={e => handleButtonSettingChange(buttonType, 'font', e.target.value)}
                className="form-select form-control"
              >
                {fontOptions}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label className="d-flex align-items-center" style={{ height: '25px' }}>
                Font color: (Transparent
                <input
                  type="checkbox"
                  className="ml-1 mr-1"
                  checked={settings?.fontColor === 'transparent'}
                  onChange={e =>
                    handleButtonSettingChange(
                      buttonType,
                      'fontColor',
                      e.target.checked ? 'transparent' : '#000000'
                    )
                  }
                />
                )
              </label>
              <MuiColorInput
                className="form-control"
                format="hex"
                value={
                  settings?.fontColor === 'transparent' ? '' : settings?.fontColor || '#000000'
                }
                onChange={newHex =>
                  handleButtonSettingChange(buttonType, 'fontColor', newHex?.toUpperCase?.())
                }
                fallbackValue="#000000"
                disabled={settings?.fontColor === 'transparent'}
                // size="small"
                isAlphaHidden={true} // This disables the alpha slider
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0px !important',
                    height: 53,
                  },
                  '& .MuiOutlinedInput-input': {
                    height: '36px',
                  },
                  '& .MuiColorInput-Button': {
                    width: '49px !important', // Increase width
                    height: '40px !important',
                  },
                  '& .MuiInputAdornment-root .MuiBox-root': {
                    width: 18,
                    height: 18,
                  },
                }}
              />
              {/* <input
                type="color"
                disabled={settings?.fontColor === "transparent"}
                value={settings?.fontColor === "transparent" ? "#000000" : (settings?.fontColor || "#000000")}
                onChange={(e) => handleButtonSettingChange(buttonType, 'fontColor', e.target.value)}
                className="form-control"
              /> */}
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label>Font Size:</label>
              <input
                type="number"
                value={settings?.fontSize || ''}
                onChange={e =>
                  handleButtonSettingChange(buttonType, 'fontSize', Number(e.target.value))
                }
                className="form-control"
              />
            </div>
          </div>
        </div>

        {/* Shadow Settings */}
        <div className="row menu-card2">
          <div className="col-md-2">
            <div className="form-group">
              <label>Shadow angle:</label>
              <input
                type="number"
                className="form-control"
                value={settings?.shadow?.angle ?? 0}
                onChange={e =>
                  handleButtonSettingChange(buttonType, 'shadow.angle', Number(e.target.value))
                }
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <label>Shadow size:</label>
              <input
                type="number"
                value={settings?.shadow?.size ?? 0}
                onChange={e =>
                  handleButtonSettingChange(buttonType, 'shadow.size', Number(e.target.value))
                }
                className="form-control"
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <label>Shadow distance:</label>
              <input
                type="number"
                value={settings?.shadow?.distance ?? 0}
                onChange={e =>
                  handleButtonSettingChange(buttonType, 'shadow.distance', Number(e.target.value))
                }
                className="form-control"
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <label>Shadow Alpha:</label>
              <input
                type="number"
                value={settings?.shadow?.alpha ?? 0}
                onChange={e =>
                  handleButtonSettingChange(buttonType, 'shadow.alpha', Number(e.target.value))
                }
                className="form-control"
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label className="d-flex align-items-center" style={{ height: '25px' }}>
                Shadow color: (Transparent
                <input
                  type="checkbox"
                  className="ml-1 mr-1"
                  checked={settings?.shadow?.color === 'transparent'}
                  onChange={e =>
                    handleButtonSettingChange(
                      buttonType,
                      'shadow.color',
                      e.target.checked ? 'transparent' : '#000000'
                    )
                  }
                />
                )
              </label>
              <MuiColorInput
                className="form-control"
                format="hex"
                value={
                  settings?.shadow?.color === 'transparent'
                    ? ''
                    : settings?.shadow?.color || '#000000'
                }
                onChange={newHex =>
                  handleButtonSettingChange(
                    buttonType,
                    'shadow.color',
                    newHex?.toUpperCase?.() || '#000000'
                  )
                }
                fallbackValue="#000000"
                disabled={settings?.shadow?.color === 'transparent'}
                // size="small"
                isAlphaHidden={true} // This disables the alpha slider
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0px !important',
                    height: 53,
                  },
                  '& .MuiOutlinedInput-input': {
                    height: '36px',
                  },
                  '& .MuiColorInput-Button': {
                    width: '49px !important', // Increase width
                    height: '40px !important',
                  },
                  '& .MuiInputAdornment-root .MuiBox-root': {
                    width: 18,
                    height: 18,
                  },
                }}
              />
              {/* <input
                type="color"
                disabled={settings?.shadow?.color === "transparent"}
                value={settings?.shadow?.color === "transparent" ? "#000000" : (settings?.shadow?.color || "#000000")}
                onChange={(e) => handleButtonSettingChange(buttonType, 'shadow.color', e.target.value)}
                className="form-control"
              /> */}
            </div>
          </div>
        </div>

        {/* Highlight Settings */}
        <div className="row menu-card2">
          <div className="col-md-4">
            <div className="form-group">
              <label className="d-flex align-items-center" style={{ height: '25px' }}>
                Highlight border color: (Transparent
                <input
                  type="checkbox"
                  className="ml-1 mr-1"
                  checked={settings?.highlight?.borderColor === 'transparent'}
                  onChange={e =>
                    handleButtonSettingChange(
                      buttonType,
                      'highlight.borderColor',
                      e.target.checked ? 'transparent' : '#000000'
                    )
                  }
                />
                )
              </label>
              <MuiColorInput
                className="form-control"
                format="hex"
                value={
                  settings?.highlight?.borderColor === 'transparent'
                    ? ''
                    : settings?.highlight?.borderColor || '#000000'
                }
                onChange={newHex =>
                  handleButtonSettingChange(
                    buttonType,
                    'highlight.borderColor',
                    newHex?.toUpperCase?.() || '#000000'
                  )
                }
                fallbackValue="#000000"
                disabled={settings?.highlight?.borderColor === 'transparent'}
                // size="small"
                isAlphaHidden={true} // This disables the alpha slider
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0px !important',
                    height: 53,
                  },
                  '& .MuiOutlinedInput-input': {
                    height: '36px',
                  },
                  '& .MuiColorInput-Button': {
                    width: '49px !important', // Increase width
                    height: '40px !important',
                  },
                  '& .MuiInputAdornment-root .MuiBox-root': {
                    width: 18,
                    height: 18,
                  },
                }}
              />
              {/* <input
                type="color"
                className="form-control"
                disabled={settings?.highlight?.borderColor === "transparent"}
                value={settings?.highlight?.borderColor === "transparent" ? "#000000" : (settings?.highlight?.borderColor || "#000000")}
                onChange={(e) => handleButtonSettingChange(buttonType, 'highlight.borderColor', e.target.value)}
              /> */}
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label className="d-flex align-items-center" style={{ height: '25px' }}>
                Highlight fill color: (Transparent
                <input
                  type="checkbox"
                  className="ml-1 mr-1"
                  checked={settings?.highlight?.fillColor === 'transparent'}
                  onChange={e =>
                    handleButtonSettingChange(
                      buttonType,
                      'highlight.fillColor',
                      e.target.checked ? 'transparent' : '#000000'
                    )
                  }
                />
                )
              </label>

              <MuiColorInput
                className="form-control"
                format="hex"
                value={
                  settings?.highlight?.fillColor === 'transparent'
                    ? ''
                    : settings?.highlight?.fillColor || '#000000'
                }
                onChange={newHex =>
                  handleButtonSettingChange(
                    buttonType,
                    'highlight.fillColor',
                    newHex?.toUpperCase?.() || '#000000'
                  )
                }
                fallbackValue="#000000"
                disabled={settings?.highlight?.fillColor === 'transparent'}
                // size="small"
                isAlphaHidden={true} // This disables the alpha slider
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0px !important',
                    height: 53,
                  },
                  '& .MuiOutlinedInput-input': {
                    height: '36px',
                  },
                  '& .MuiColorInput-Button': {
                    width: '49px !important', // Increase width
                    height: '40px !important',
                  },
                  '& .MuiInputAdornment-root .MuiBox-root': {
                    width: 18,
                    height: 18,
                  },
                }}
              />
              {/* <input
                type="color"
                disabled={settings?.highlight?.fillColor === "transparent"}
                className="form-control"
                value={settings?.highlight?.fillColor === "transparent" ? "#000000" : (settings?.highlight?.fillColor || "#000000")}
                onChange={(e) => handleButtonSettingChange(buttonType, 'highlight.fillColor', e.target.value)}
              /> */}
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label className="d-flex align-items-center" style={{ height: '25px' }}>
                Highlight Font color: (Transparent
                <input
                  type="checkbox"
                  className="ml-1 mr-1"
                  checked={settings?.highlight?.fontColor === 'transparent'}
                  onChange={e =>
                    handleButtonSettingChange(
                      buttonType,
                      'highlight.fontColor',
                      e.target.checked ? 'transparent' : '#000000'
                    )
                  }
                />
                )
              </label>
              <MuiColorInput
                className="form-control"
                format="hex"
                value={
                  settings?.highlight?.fontColor === 'transparent'
                    ? ''
                    : settings?.highlight?.fontColor || '#000000'
                }
                onChange={newHex =>
                  handleButtonSettingChange(
                    buttonType,
                    'highlight.fontColor',
                    newHex?.toUpperCase?.() || '#000000'
                  )
                }
                fallbackValue="#000000"
                disabled={settings?.highlight?.fontColor === 'transparent'}
                // size="small"
                isAlphaHidden={true} // This disables the alpha slider
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0px !important',
                    height: 53,
                  },
                  '& .MuiOutlinedInput-input': {
                    height: '36px',
                  },
                  '& .MuiColorInput-Button': {
                    width: '49px !important', // Increase width
                    height: '40px !important',
                  },
                  '& .MuiInputAdornment-root .MuiBox-root': {
                    width: 18,
                    height: 18,
                  },
                }}
              />
              {/* <input
                type="color"
                disabled={settings?.highlight?.fontColor === "transparent"}
                className="form-control"
                value={settings?.highlight?.fontColor === "transparent" ? "#000000" : (settings?.highlight?.fontColor || "#000000")}
                onChange={(e) => handleButtonSettingChange(buttonType, 'highlight.fontColor', e.target.value)}
              /> */}
            </div>
          </div>
        </div>

        {/* Click Sound Settings */}
        {/* <div className="row menu-card2">
          <div className="col-md-6">
            <div className="form-group">
              <label>Upload Sound File:</label><br />
              <input 
                type="file" 
                accept="audio/*"
                onChange={(e) => handleFileChange(buttonType, e.target.files[0])} 
                disabled={isUploading}
              />
              {file && (
                <button 
                  type="button" 
                  style={{backgroundColor: buttonColor, marginLeft: '10px'}} 
                  className="btn btn-primary btn-sm"
                  onClick={() => handleUpload(buttonType)}
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Upload'}
                </button>
              )}
              {uploadStatus && (
                <div className="mt-2">
                  <small className={uploadStatus.includes('success') ? 'text-success' : 'text-danger'}>
                    {uploadStatus}
                  </small>
                </div>
              )}
              <div className="mt-2">
                {settings?.clickSound && (
                  <a 
                    href={settings.clickSound}
                    target='_blank'
                    rel="noreferrer"
                    className="text-primary"
                  >
                    {settings.clickSoundFilename || 'Current Sound File'}
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>Click sound volume <strong>(Range: 0 to 1)</strong>:</label>
              <input
                type="number"
                className="form-control"
                value={settings?.clickSoundVolume ?? 0}
                min="0"
                max="1"
                step="0.1"
                onChange={(e) => {
                  let value = Number(e.target.value);
                  if (value < 0) value = 0;
                  if (value > 1) value = 1;
                  handleButtonSettingChange(buttonType, 'clickSoundVolume', value);
                }}
              />
            </div>
          </div>
        </div> */}
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h4>Button Settings Configuration</h4>
          <button type="button" className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* Button Type Selector */}
          <div className="button-type-selector mb-4">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn ${activeButtonType === 'back' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveButtonType('back')}
                style={{
                  backgroundColor: activeButtonType === 'back' ? buttonColor : 'transparent',
                  borderColor: buttonColor,
                  color: activeButtonType === 'back' ? 'white' : buttonColor,
                }}
              >
                Back Button Settings
              </button>
              <button
                type="button"
                className={`btn ${activeButtonType === 'next' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveButtonType('next')}
                style={{
                  backgroundColor: activeButtonType === 'next' ? buttonColor : 'transparent',
                  borderColor: buttonColor,
                  color: activeButtonType === 'next' ? 'white' : buttonColor,
                }}
              >
                Next Button Settings
              </button>
            </div>
          </div>

          {/* Settings Content */}
          <div className="settings-content">
            <h5 className="mb-3">
              {activeButtonType === 'back' ? 'Back' : 'Next'} Button Configuration
            </h5>
            {renderButtonSettings(activeButtonType)}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary mr-2" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ backgroundColor: buttonColor, borderColor: buttonColor }}
            onClick={handleSave}
          >
            Save Settings
          </button>
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-container {
          background: white;
          border-radius: 8px;
          width: 100%;
          max-width: 1200px;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }

        .modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid #e0e0e0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background-color: #f8f9fa;
        }

        .modal-header h4 {
          margin: 0;
          color: #333;
          font-weight: 600;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s;
        }

        .close-button:hover {
          background-color: #f0f0f0;
          color: #333;
        }

        .modal-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }

        .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #e0e0e0;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          background-color: #f8f9fa;
        }

        .button-type-selector {
          text-align: center;
        }

        .settings-content {
          background-color: #fff;
        }

        .menu-card2 {
          margin-bottom: 20px;
          padding: 20px;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          background-color: #f9f9f9;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .form-group label {
          font-weight: 500;
          margin-bottom: 0.5rem;
          color: #333;
          display: block;
        }

        .form-control {
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 8px 12px;
          font-size: 14px;
          transition: border-color 0.2s;
        }

        .form-control:focus {
          border-color: ${buttonColor};
          outline: none;
          box-shadow: 0 0 0 2px ${buttonColor}20;
        }

        .btn {
          padding: 8px 16px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          border: 1px solid;
        }

        .btn-primary {
          background-color: ${buttonColor};
          border-color: ${buttonColor};
          color: white;
        }

        .btn-primary:hover {
          background-color: ${buttonColor}dd;
          border-color: ${buttonColor}dd;
        }

        .btn-secondary {
          background-color: #6c757d;
          border-color: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background-color: #5a6268;
          border-color: #545b62;
        }

        .btn-outline-primary {
          background-color: transparent;
          border-color: ${buttonColor};
          color: ${buttonColor};
        }

        .btn-outline-primary:hover {
          background-color: ${buttonColor};
          color: white;
        }

        .text-success {
          color: #28a745 !important;
        }

        .text-danger {
          color: #dc3545 !important;
        }

        .text-primary {
          color: ${buttonColor} !important;
        }

        .btn-sm {
          padding: 4px 8px;
          font-size: 12px;
        }

        .mr-2 {
          margin-right: 0.5rem;
        }

        .mb-3 {
          margin-bottom: 1rem;
        }

        .mb-4 {
          margin-bottom: 1.5rem;
        }

        .mt-2 {
          margin-top: 0.5rem;
        }

        .ml-1 {
          margin-left: 0.25rem;
        }

        .mr-1 {
          margin-right: 0.25rem;
        }

        @media (max-width: 768px) {
          .modal-container {
            margin: 10px;
            max-height: 95vh;
          }

          .modal-body {
            padding: 16px;
          }

          .menu-card2 {
            padding: 15px;
          }

          .row {
            margin: 0 -8px;
          }

          .col-md-2,
          .col-md-3,
          .col-md-4,
          .col-md-6 {
            padding: 0 8px;
            margin-bottom: 15px;
          }
        }
      `}</style>
    </div>
  );
};

export default AirtableButtonSettingsModal;
